/**
 * @file interpret/engine/s2sApi.ts
 * @brief Session start / end / cancel: the phone's `useS2S` REST calls.
 *
 * @details
 * Same endpoints and the same billing as the phone (DES, 01-10-2026: one price
 * on every platform). `/start` places the card hold and returns the rate;
 * `/end` charges `total_seconds`, the pause-excluded time, which the server
 * clamps to wall-clock so a client can only ever lower the bill; `/cancel`
 * releases the hold with no charge (used when the socket never connects).
 *
 * `/start` answers 200 with `error: "MISSING_PAYMENT_METHOD"` (or a decline code)
 * instead of an HTTP error; that is turned into a thrown `ApiClientError` here
 * so the screen handles every failure in one place.
 *
 * @author DES
 * @date 01-10-2026
 */

import { z } from "zod";
import { ApiClientError, apiPost } from "@/shared/api/client";
import { s2sEndpoints } from "@/shared/api/endpoints";
import { tokenStorage } from "@/shared/api/tokenStorage";
import { env } from "@/shared/config/env";
import { authRequiredDetails, confirmWithBank } from "@/shared/stripe/confirmWithBank";

export type StartSessionInput = {
  srcLang: string;
  tgtLang: string;
  srcLocale?: string;
  tgtLocale?: string;
  personAName?: string;
  personBName?: string;
  personAGender?: string;
  personBGender?: string;
  referenceNumber?: string;
  billingContext?: "personal" | "corporate";
  scheduledSessionId?: string;
  /**
   * The other person's answer on the voice consent sheet (04-10-2026):
   * true = keep their original voice, false = don't. Omit when not asked.
   * Related: ../components/VoiceConsentSheet.tsx, BACKEND s2s/voiceConsent.mjs
   */
  personBVoiceConsent?: boolean;
};

const StartSchema = z.object({
  conversation_id: z.string().optional(),
  error: z.string().optional(),
  message: z.string().optional(),
  currencyCode: z.string().optional(),
  ratePerMin: z.number().optional(),
  minChargeMajor: z.number().optional(),
});

export type StartedSession = {
  conversationId: string;
  currencyCode: string | null;
  ratePerMin: number | null;
  minChargeMajor: number | null;
};

/** "ur-PK" → "ur". */
function baseLang(code: string): string {
  return code.split(/[-_]/)[0]!.toLowerCase();
}

export async function startS2sSession(input: StartSessionInput): Promise<StartedSession> {
  const first = await postStart(input, undefined);
  // 3DS on the card hold (18-10-2026): `/start` answers 200 AUTH_REQUIRED with
  // the hold's clientSecret. Run the bank's challenge in the page, then start
  // again naming that PaymentIntent; the server adopts it instead of a second hold.
  const challenge = authRequiredDetails(first);
  if (!challenge) return toStarted(first);
  const bank = await confirmWithBank(challenge.clientSecret, challenge.paymentIntentId);
  if (!bank.ok) throw new ApiClientError(bank.message, 402, bank.code, first);
  return toStarted(await postStart(input, bank.paymentIntentId));
}

async function postStart(input: StartSessionInput, paymentIntentId: string | undefined): Promise<unknown> {
  return apiPost<unknown>(s2sEndpoints.start, {
    // The base code, as the phone sends ("ur", not "ur-PK"); the locale goes
    // in src_locale. The server's Urdu-script fix compared the language to
    // "ur" exactly, so web sessions kept Hindi-script Urdu (03-10-2026).
    src_lang: baseLang(input.srcLang),
    tgt_lang: baseLang(input.tgtLang),
    src_locale: input.srcLocale,
    tgt_locale: input.tgtLocale,
    person_a_name: input.personAName,
    person_b_name: input.personBName,
    person_a_gender: input.personAGender,
    person_b_gender: input.personBGender,
    reference_number: input.referenceNumber,
    billing_context: input.billingContext,
    scheduled_session_id: input.scheduledSessionId,
    session_kind: "standard",
    person_b_voice_consent: input.personBVoiceConsent,
    ...(paymentIntentId ? { payment_intent_id: paymentIntentId } : {}),
  });
}

function toStarted(raw: unknown): StartedSession {
  const data = StartSchema.parse(raw);
  if (data.error || !data.conversation_id) {
    throw new ApiClientError(
      data.message || "We couldn't start the session. Please try again.",
      402,
      data.error || "S2S_START_FAILED",
      raw,
    );
  }
  return {
    conversationId: data.conversation_id,
    currencyCode: data.currencyCode ?? null,
    ratePerMin: data.ratePerMin ?? null,
    minChargeMajor: data.minChargeMajor ?? null,
  };
}

// ----------------------------------------------------------------------
// Other person's voice consent sheet (04-10-2026). Same endpoint and copy as
// the phone's VoiceConsentSheet: the text in THEIR language + a spoken clip.
// ----------------------------------------------------------------------
const ConsentTextSchema = z.object({
  title: z.string(),
  body: z.string(),
  instruction: z.string().optional(),
  keep: z.string(),
  decline: z.string(),
  listen: z.string().optional(),
});

const VoiceConsentPromptSchema = z.object({
  lang: z.string(),
  locale: z.string().nullable().optional(),
  translated: z.boolean(),
  text: ConsentTextSchema,
  english: ConsentTextSchema,
  audio_b64: z.string().nullable().optional(),
  audio_mime: z.string().nullable().optional(),
});

export type VoiceConsentPrompt = z.infer<typeof VoiceConsentPromptSchema>;

export async function fetchVoiceConsentPrompt(input: {
  lang: string;
  locale?: string;
  gender?: string | null;
}): Promise<VoiceConsentPrompt> {
  const raw = await apiPost<unknown>(s2sEndpoints.voiceConsentPrompt, {
    lang: baseLang(input.lang),
    locale: input.locale,
    gender: input.gender ?? undefined,
  });
  return VoiceConsentPromptSchema.parse(raw);
}

export async function endS2sSession(conversationId: string, totalSeconds: number): Promise<void> {
  // The server finalizes billing and the transcript; allow it time.
  await apiPost<unknown>(
    s2sEndpoints.end,
    { conversation_id: conversationId, total_seconds: Math.max(0, Math.round(totalSeconds)) },
    { timeout: 60_000 },
  );
}

/**
 * Cancel. Since BACKEND 00145 (17-10-2026) a session in which someone spoke is
 * ended and billed for the time used instead (`billed: true`); the caller says
 * so rather than "you have not been charged".
 */
export async function cancelS2sSession(conversationId: string): Promise<{ billed: boolean }> {
  const raw = await apiPost<unknown>(s2sEndpoints.cancel, { conversation_id: conversationId });
  return { billed: !!(raw && typeof raw === "object" && (raw as { billed?: unknown }).billed === true) };
}

/**
 * Fire-and-forget `/end` that survives the tab closing (`fetch` keepalive; a
 * beacon cannot carry the Bearer header). Used from `pagehide` only: the user
 * closed the tab mid-session, so bill the time used now instead of leaving the
 * session to the 30-minute sweeper.
 */
export function endS2sSessionOnUnload(conversationId: string, totalSeconds: number): void {
  const token = tokenStorage.get();
  try {
    void fetch(`${env.apiBaseUrl}${s2sEndpoints.end}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        total_seconds: Math.max(0, Math.round(totalSeconds)),
      }),
    }).catch(() => undefined);
  } catch {
    /* the sweeper is the backstop */
  }
}
